import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, FileText, Briefcase, ShoppingCart, Clock, Receipt, Plane, ArrowLeft, Search } from "lucide-react";
import { useOrgCurrency } from "@/lib/currency-context";

const API = process.env.REACT_APP_API || "";
export function Kunde360View({ kundeId, onBack }) {
  const { formatMoney } = useOrgCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("uebersicht");
  const headers = useCallback(() => { const t = localStorage.getItem("token") || sessionStorage.getItem("token"); return { Authorization: `Bearer ${t}` }; }, []);

  useEffect(() => {
    if (!kundeId) return;
    (async () => {
      try { const r = await axios.get(`${API}/api/360/kunde/${kundeId}`, { headers: headers() }); setData(r.data); } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [kundeId, headers]);

  if (loading) return <p className="text-sm text-gray-400 p-4">Lade Kundenprofil...</p>;
  if (!data) return <p className="text-sm text-gray-400 p-4">Kunde nicht gefunden.</p>;

  const { kunde, zusammenfassung: z, timeline } = data;
  const tabs = [
    { id: "uebersicht", label: "Übersicht" },
    { id: "angebote", label: `Angebote (${z.anzahl_angebote})` },
    { id: "rechnungen", label: `Rechnungen (${z.anzahl_rechnungen})` },
    { id: "projekte", label: `Projekte (${z.anzahl_projekte})` },
    { id: "bestellungen", label: `Bestellungen (${z.anzahl_bestellungen})` },
    { id: "timeline", label: "Timeline" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {onBack && <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Button>}
        <div>
          <h2 className="text-xl font-bold">{kunde.kunde_name || kunde.kunde_firma}</h2>
          <p className="text-sm text-gray-500">{kunde.kunde_email} | {kunde.kunde_telefon}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-xs text-gray-500">Offene Forderungen</div><div className="text-lg font-bold text-red-600">{formatMoney(z.offene_forderungen)}</div></Card>
        <Card className="p-3"><div className="text-xs text-gray-500">Bezahlt (gesamt)</div><div className="text-lg font-bold text-green-600">{formatMoney(z.bezahlt_gesamt)}</div></Card>
        <Card className="p-3"><div className="text-xs text-gray-500">Projekte</div><div className="text-lg font-bold">{z.anzahl_projekte}</div></Card>
        <Card className="p-3"><div className="text-xs text-gray-500">Arbeitsstunden</div><div className="text-lg font-bold">{z.gesamt_stunden} h</div></Card>
      </div>

      {/* Sub-Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b pb-2">
        {tabs.map(t => <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-3 py-1 rounded-md text-sm whitespace-nowrap ${subTab === t.id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 text-gray-600"}`}>{t.label}</button>)}
      </div>

      {/* Content */}
      {subTab === "uebersicht" && (
        <Card className="p-4"><h3 className="font-semibold mb-2">Stammdaten</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500">Firma:</span> {kunde.kunde_firma}</div>
            <div><span className="text-gray-500">Adresse:</span> {kunde.kunde_strasse}, {kunde.kunde_plz} {kunde.kunde_ort}</div>
            <div><span className="text-gray-500">E-Mail:</span> {kunde.kunde_email}</div>
            <div><span className="text-gray-500">Telefon:</span> {kunde.kunde_telefon}</div>
          </div>
        </Card>
      )}
      {subTab === "angebote" && <DataList items={data.angebote} formatMoney={formatMoney} type="angebot" />}
      {subTab === "rechnungen" && <DataList items={data.rechnungen} formatMoney={formatMoney} type="rechnung" />}
      {subTab === "projekte" && <DataList items={data.projekte} type="projekt" />}
      {subTab === "bestellungen" && <DataList items={data.bestellungen} type="bestellung" />}
      {subTab === "timeline" && <TimelineView items={timeline} />}
    </div>
  );
}

const DataList = ({ items, formatMoney, type }) => (
  <div className="space-y-2">{items.length === 0 ? <p className="text-gray-400 text-sm">Keine Einträge</p> : items.map((item, i) => (
    <Card key={i} className="p-3 flex justify-between items-center">
      <div><div className="text-sm font-medium">{item.angebot_nr || item.bestellnummer || item.name || "–"}</div><div className="text-xs text-gray-500">{item.status || ""}</div></div>
      <div className="text-sm font-medium">{item.total ? formatMoney(item.total, item.currency) : item.betrag ? formatMoney(item.betrag, item.currency) : ""}</div>
    </Card>
  ))}</div>
);

const TimelineView = ({ items }) => (
  <div className="space-y-2">{items.length === 0 ? <p className="text-gray-400 text-sm">Keine Einträge</p> : items.map((item, i) => (
    <div key={i} className="flex items-center gap-3 text-sm border-l-2 border-gray-200 pl-3 py-1">
      <span className="text-xs text-gray-400 w-20 shrink-0">{item.datum}</span>
      <span className="font-medium">{item.titel}</span>
      <span className="text-xs text-gray-400 ml-auto">{item.studio}</span>
    </div>
  ))}</div>
);

export function KundenSucheModal({ onSelect, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const headers = () => { const t = localStorage.getItem("token") || sessionStorage.getItem("token"); return { Authorization: `Bearer ${t}` }; };

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try { const r = await axios.get(`${API}/api/360/kunden`, { headers: headers(), params: { q } }); setResults(r.data || []); } catch (e) {}
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
      <Card className="w-full max-w-md p-4 space-y-3">
        <div className="flex items-center gap-2"><Search className="w-4 h-4" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Kunde suchen..." autoFocus /></div>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {results.map((c, i) => <button key={i} onClick={() => onSelect(c.id)} className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">{c.kunde_name || c.kunde_firma} – {c.kunde_email}</button>)}
        </div>
        <Button variant="outline" onClick={onClose} className="w-full">Schließen</Button>
      </Card>
    </div>
  );
}
